import { useCallback, useRef, useState } from "react";
import { chatStream, type BuildAgentSSEEvent, type GraphDiffProposal, type FileDiffProposal } from "../api/buildAgent";

export interface BuildAgentChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  toolCalls?: Array<{ name: string; input: Record<string, unknown> }>;
  // Graph-level proposals (Agent Studio / Workflow Studio)
  proposal?: GraphDiffProposal;
  proposalStatus?: "pending" | "accepted" | "rejected";
  // File-level proposals (Code Agent — Phase 5)
  fileDiffProposal?: FileDiffProposal;
  fileDiffStatus?: "pending" | "accepted" | "rejected";
  timestamp: string;
}

export interface BuildAgentState {
  messages: BuildAgentChatMessage[];
  conversationId: string | null;
  isStreaming: boolean;
  error: string | null;
}

/**
 * Hook for the Build Agent chat interface.
 * Modeled on useGraphRun — streams SSE events and manages message state.
 */
export function useBuildAgent(companyId: string) {
  const [state, setState] = useState<BuildAgentState>({
    messages: [],
    conversationId: null,
    isStreaming: false,
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);
  const currentAssistantIdRef = useRef<string>("");

  const sendMessage = useCallback(
    (message: string, graphId: string, modelId?: string, mode?: "graph" | "workflow" | "code") => {
      abortRef.current?.abort();

      const userMsg: BuildAgentChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: message,
        timestamp: new Date().toISOString(),
      };

      const assistantId = crypto.randomUUID();
      currentAssistantIdRef.current = assistantId;

      const assistantMsg: BuildAgentChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
      };

      setState((s) => ({
        ...s,
        messages: [...s.messages, userMsg, assistantMsg],
        isStreaming: true,
        error: null,
      }));

      const controller = chatStream(
        companyId,
        {
          message,
          graphId,
          conversationId: state.conversationId ?? undefined,
          modelId,
          mode,
        },
        (event: BuildAgentSSEEvent) => {
          const aid = assistantId;

          if (event.type === "message") {
            setState((s) => ({
              ...s,
              messages: s.messages.map((m) =>
                m.id === aid ? { ...m, content: m.content + event.content } : m,
              ),
            }));
          }

          if (event.type === "tool_call") {
            setState((s) => ({
              ...s,
              messages: s.messages.map((m) =>
                m.id === aid
                  ? {
                      ...m,
                      toolCalls: [
                        ...(m.toolCalls ?? []),
                        { name: event.name, input: event.input },
                      ],
                    }
                  : m,
              ),
            }));
          }

          if (event.type === "graph_diff") {
            const proposal: GraphDiffProposal = {
              proposalId: event.proposalId,
              description: event.description,
              operations: event.operations,
              summary: event.summary,
            };
            setState((s) => ({
              ...s,
              messages: s.messages.map((m) =>
                m.id === aid ? { ...m, proposal, proposalStatus: "pending" } : m,
              ),
            }));
          }

          if (event.type === "file_diff") {
            const fileDiffProposal: FileDiffProposal = {
              proposalId: event.proposalId,
              type: "file_diff",
              filePath: event.filePath,
              description: event.description,
              operationType: event.operationType,
              fileOldContent: event.fileOldContent,
              fileContent: event.fileContent,
            };
            setState((s) => ({
              ...s,
              messages: s.messages.map((m) =>
                m.id === aid
                  ? { ...m, fileDiffProposal, fileDiffStatus: "pending" }
                  : m,
              ),
            }));
          }

          if (event.type === "done") {
            setState((s) => ({
              ...s,
              isStreaming: false,
              conversationId: event.conversationId ?? s.conversationId,
            }));
          }

          if (event.type === "error") {
            setState((s) => ({
              ...s,
              isStreaming: false,
              error: event.message,
              messages: s.messages.map((m) =>
                m.id === aid
                  ? { ...m, content: m.content || `Error: ${event.message}` }
                  : m,
              ),
            }));
          }
        },
      );

      abortRef.current = controller;
    },
    [companyId, state.conversationId],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setState((s) => ({ ...s, isStreaming: false }));
  }, []);

  const markProposal = useCallback(
    (proposalId: string, status: "accepted" | "rejected") => {
      setState((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.proposal?.proposalId === proposalId ? { ...m, proposalStatus: status } : m,
        ),
      }));
    },
    [],
  );

  const markFileDiff = useCallback(
    (proposalId: string, status: "accepted" | "rejected") => {
      setState((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.fileDiffProposal?.proposalId === proposalId
            ? { ...m, fileDiffStatus: status }
            : m,
        ),
      }));
    },
    [],
  );

  const loadConversation = useCallback(
    (conversationId: string, messages: BuildAgentChatMessage[]) => {
      setState({ messages, conversationId, isStreaming: false, error: null });
    },
    [],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setState({ messages: [], conversationId: null, isStreaming: false, error: null });
  }, []);

  return {
    ...state,
    sendMessage,
    stop,
    markProposal,
    markFileDiff,
    loadConversation,
    reset,
  };
}
